/* eslint-disable react-refresh/only-export-components */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class WebHotelierErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      'WebHotelier Error Boundary caught an error:',
      error,
      errorInfo
    );

    this.setState({
      error,
      errorInfo,
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to external service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Integrate with error tracking service (Sentry, etc.)
      console.error('[WebHotelier Error]', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
    }
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-64 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-900">
                    WebHotelier Integration Error
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    There was an issue connecting to WebHotelier services
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-red-100 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800 mb-1">
                    Error Details:
                  </p>
                  <p className="text-sm text-red-700 font-mono break-all">
                    {this.state.error?.message || 'Unknown error occurred'}
                  </p>
                </div>

                <div className="flex items-center gap-2 text-sm text-red-600">
                  <WifiOff className="h-4 w-4" />
                  <span>Operating in offline mode</span>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={this.handleRetry}
                    disabled={this.state.retryCount >= 3}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    {this.state.retryCount >= 3
                      ? 'Max Retries Reached'
                      : 'Retry Connection'}
                  </button>

                  <button
                    onClick={() => window.location.reload()}
                    className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Wifi className="h-4 w-4" />
                    Reload Page
                  </button>
                </div>

                {this.state.retryCount > 0 && (
                  <p className="text-xs text-red-600">
                    Retry attempts: {this.state.retryCount}/3
                  </p>
                )}
              </div>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="mt-4 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Development Debug Info
                </summary>
                <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                  <div className="mb-2">
                    <strong>Error Stack:</strong>
                    <pre className="mt-1 bg-gray-100 dark:bg-slate-800 p-2 rounded overflow-auto">
                      {this.state.error?.stack}
                    </pre>
                  </div>
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 bg-gray-100 dark:bg-slate-800 p-2 rounded overflow-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easy wrapping
export function withWebHotelierErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <WebHotelierErrorBoundary fallback={fallback}>
        <Component {...props} />
      </WebHotelierErrorBoundary>
    );
  };
}

export default WebHotelierErrorBoundary;
